import { DropdownMenuItem } from '@heidi/design-system/dropdown-menu';
import { Switch } from '@heidi/design-system/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { Sparkles } from 'lucide-react';
import { useIntl } from 'react-intl';

import { useSessionLayoutV2MenuToggle } from '@/features/settings/heidi-labs/early-access/use-session-layout-v2-menu-toggle';

import { sideNavFooterMessages } from './SideNavFooter.messages';

/**
 * Avatar-menu shortcut for the manual Session Page V2 Labs opt-in.
 *
 * Keeping the dropdown open while the mutation resolves gives the switch's
 * optimistic state an accessible home. On a session route, the successful
 * opt-in renders V2 and its existing one-time tour queue launches the tour
 * when it has not already been completed.
 */
interface SessionLayoutV2MenuToggleProps {
  isOfflineGated: boolean;
  offlineTooltip: string;
}

export const SessionLayoutV2MenuToggle = ({
  isOfflineGated,
  offlineTooltip,
}: SessionLayoutV2MenuToggleProps) => {
  const intl = useIntl();
  const { isVisible, isEnabled, isPending, setEnabled } = useSessionLayoutV2MenuToggle();

  if (!isVisible) return null;

  const menuItem = (
    <DropdownMenuItem
      // A nested Switch emits the item's select event too. Prevent the default
      // close so users receive the optimistic state and any mutation failure
      // before intentionally dismissing the account menu.
      disabled={isOfflineGated}
      onSelect={(event) => event.preventDefault()}
      className="gap-2"
      data-testid="sidebar-menu-new-layout"
    >
      <Sparkles />
      <span className="flex-1">
        {intl.formatMessage(sideNavFooterMessages.newLayout)}
      </span>
      <Switch
        size="xs"
        checked={isEnabled}
        disabled={isPending || isOfflineGated}
        onCheckedChange={setEnabled}
        aria-label={intl.formatMessage(sideNavFooterMessages.newLayoutToggle)}
      />
    </DropdownMenuItem>
  );

  if (!isOfflineGated) return menuItem;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* The disabled item has pointer-events-none, so the wrapper owns hover. */}
        <span className="block">{menuItem}</span>
      </TooltipTrigger>
      <TooltipContent>{offlineTooltip}</TooltipContent>
    </Tooltip>
  );
};
